import { useCallback, useEffect, useRef, useState } from 'react';
import { PublicationSearchResultItem } from '@amzn/awscat-pattern-library-service-model';
import { convertEntities } from '@amzn/awscat-editor';

import { getNodeHref } from '../../api/ContentUtil';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import {
  duplicateSearchTimerDelay,
  longTitleInputMaxLength,
  longTitleInputMinLength,
  shortTitleInputMaxLength,
  shortTitleInputMinLength,
} from '../../api/UtilsApi';
import { deepCloneContent } from '../../model/CloneContent';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { usePendingFlushCoordinator } from '../../contexts/PendingFlushCoordinatorContext';
import { ContentEditorService } from '../../services/ContentEditorService';
import { useContent } from '../v2-hooks/useContent';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';
import { decodeSearchtextString } from '../utils/Search';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';
import { log } from '../../loggers/Logger';

/** State and actions for a single title input field (long title or short title). */
export interface TitleFieldViewModel {
  readonly value: string;
  readonly convertedLength: number;
  readonly validationError: string | undefined;
  readonly isSearchingDuplicates: boolean;
  readonly duplicates: readonly PublicationSearchResultItem[];
  /** True when the duplicate check itself failed — the UI must not read this as "no duplicates". */
  readonly isDuplicateCheckFailed: boolean;
  readonly handleChange: (text: string) => void;
  readonly handleBlur: () => void;
}

export interface ContentTitleHeadingViewModel {
  readonly longTitle: TitleFieldViewModel;
  readonly shortTitle: TitleFieldViewModel;
}

/**
 * Returns a validation error message when the converted title length is outside the allowed range.
 * An empty value is valid for an optional field.
 */
function validateTitle(
  convertedLength: number,
  min: number,
  max: number,
  isOptional: boolean,
): string | undefined {
  if (isOptional && convertedLength === 0) return undefined;
  if (convertedLength < min || convertedLength > max) {
    return `${min} - ${max} characters required`;
  }
  return undefined;
}

/**
 * Searches for publications with a matching title to detect potential duplicates.
 *
 * When the title changes:
 *  - If empty, clears duplicates immediately and stops searching.
 *  - Otherwise, shows a loading state and debounces the search API call.
 *  - When the debounce fires, calls the search API and filters out the current document.
 *  - If a newer title change arrives before the API responds, the stale response is discarded.
 *  - On API error, flags the check as failed rather than implying "no duplicates".
 */
function useDuplicationCheck(
  title: string,
  mpl: IMplServiceClient,
  contentId: string | undefined,
  convertEntitiesFn: (text: string) => string,
) {
  const [isSearchingDuplicates, setIsSearchingDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState<PublicationSearchResultItem[]>([]);
  const [isDuplicateCheckFailed, setIsDuplicateCheckFailed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestIdRef = useRef(0);

  useEffect(() => {
    // When title is empty, no duplicates are possible — reset state.
    if (!title) {
      // Invalidate any in-flight request so its stale response is discarded.
      ++requestIdRef.current;
      setDuplicates([]);
      setIsDuplicateCheckFailed(false);
      setIsSearchingDuplicates(false);
      return;
    }

    // When a new title arrives, show loading and debounce the API call.
    setIsSearchingDuplicates(true);
    setIsDuplicateCheckFailed(false);
    clearTimeout(timerRef.current);
    const currentRequestId = ++requestIdRef.current;

    timerRef.current = setTimeout(async () => {
      try {
        const searchText = decodeSearchtextString(convertEntitiesFn(title));
        const result = await mpl.searchPublicationsByField(searchText, 'name');

        // When a newer request has been issued, discard this stale response.
        if (requestIdRef.current !== currentRequestId) return;

        // Exclude the current document from duplicates (it's not a duplicate of itself).
        const filtered = contentId
          ? result.publications.filter(
              (p: PublicationSearchResultItem) => p.contentId !== contentId,
            )
          : result.publications;
        setDuplicates(filtered);
      } catch (e) {
        log.error({ err: e }, 'Duplication check failed');
        if (requestIdRef.current !== currentRequestId) return;
        // Don't set duplicates=[] — that would falsely read as "no duplicates".
        // Flag the failure so the UI shows a "couldn't verify" note instead.
        setIsDuplicateCheckFailed(true);
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsSearchingDuplicates(false);
        }
      }
    }, duplicateSearchTimerDelay);

    return () => clearTimeout(timerRef.current);
  }, [mpl, contentId, title, convertEntitiesFn]);

  return { isSearchingDuplicates, duplicates, isDuplicateCheckFailed };
}

/** How long to wait after the last keystroke before flushing (persisting) the staged edit. */
const FLUSH_DEBOUNCE_MS = 3000;

/**
 * Manages a single title field: local state, validation, duplicate checking, and auto-save.
 *
 * Lifecycle:
 *  - When the user types (handleChange): update local value, clear validation errors,
 *    stage the edit if valid, and schedule a debounced flush (auto-save after 3s of inactivity).
 *  - When the user leaves the field (handleBlur): immediately validate, stage if valid, and flush.
 *  - When the external initial value changes (e.g., content reloaded): sync local state.
 */
function useTitleField(
  service: ContentEditorService,
  initialValue: string,
  min: number,
  max: number,
  buildStagedContent: (trimmedValue: string) => void,
  mpl: IMplServiceClient,
  contentId: string | undefined,
  convertEntitiesFn: (text: string) => string,
  isDuplicateCheckEnabled = true,
  isOptional = false,
): TitleFieldViewModel {
  const flushCoordinator = usePendingFlushCoordinator();
  const [value, setValue] = useState(initialValue);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastStagedRef = useRef(initialValue);

  // Latest value, read by the close-time flusher without re-registering per keystroke.
  const valueRef = useRef(value);
  valueRef.current = value;

  // When the external initial value changes, reset local state to stay in sync.
  useEffect(() => {
    setValue(initialValue);
    lastStagedRef.current = initialValue;
  }, [initialValue]);

  const convertedLength = convertEntitiesFn(value).length;

  const { isSearchingDuplicates, duplicates, isDuplicateCheckFailed } = useDuplicationCheck(
    isDuplicateCheckEnabled ? value : '',
    mpl,
    contentId,
    convertEntitiesFn,
  );

  // Stages the edit only when the trimmed value is valid and different from the last staged value.
  const stageIfValid = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      const len = convertEntities(trimmed).length;
      const error = validateTitle(len, min, max, isOptional);
      if (error || trimmed === lastStagedRef.current) return;
      lastStagedRef.current = trimmed;
      buildStagedContent(trimmed);
    },
    [min, max, isOptional, buildStagedContent],
  );

  // Resets and restarts the flush timer so the service persists the edit after inactivity.
  const scheduleFlush = useCallback(() => {
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(() => {
      service.flush();
    }, FLUSH_DEBOUNCE_MS);
  }, [service]);

  // When the user types: update value, clear errors, stage if valid, schedule auto-save.
  const handleChange = useCallback(
    (text: string) => {
      setValue(text);
      setValidationError(undefined);
      stageIfValid(text);
      scheduleFlush();
    },
    [stageIfValid, scheduleFlush],
  );

  // When the user leaves the field: validate immediately, stage if valid, flush right away.
  const handleBlur = useCallback(() => {
    clearTimeout(flushTimerRef.current);
    const trimmed = value.trim();
    const len = convertEntities(trimmed).length;
    const error = validateTitle(len, min, max, isOptional);
    setValidationError(error);
    if (!error && trimmed !== lastStagedRef.current) {
      lastStagedRef.current = trimmed;
      buildStagedContent(trimmed);
    }
    service.flush();
  }, [value, min, max, isOptional, service, buildStagedContent]);

  useEffect(() => {
    return () => clearTimeout(flushTimerRef.current);
  }, []);

  // While mounted, let a browser-close drain stage the current title. Staging
  // (not the async flush) is what survives close; Layer 2 persists it.
  useEffect(() => {
    return flushCoordinator.registerFlusher(() => stageIfValid(valueRef.current));
  }, [flushCoordinator, stageIfValid]);

  return {
    value,
    convertedLength,
    validationError,
    isSearchingDuplicates,
    duplicates,
    isDuplicateCheckFailed,
    handleChange,
    handleBlur,
  };
}

/**
 * Top-level view model for the content title heading section.
 * Wires up the long title (with duplicate checking) and short title (without duplicate checking).
 *
 * When the long title is staged, it also recomputes the content href from the new name.
 * When the short title is staged, it also recomputes the content href from the new short name.
 */
export function useContentTitleHeadingViewModel(): ContentTitleHeadingViewModel | undefined {
  const service = useContentEditorService();
  const mpl = useMplServiceClient();
  const convertEntitiesFn = useEntityConverter();
  const content = useContent();
  const document = useLoadedDocument();

  const initialLongTitle = content?.contentMetadata?.contentName ?? '';
  const initialShortTitle = content?.contentMetadata?.contentShortName ?? '';
  const contentId = document?.contentId;

  // Builds a staged content snapshot with the updated long title and recomputed href.
  const buildLongTitleStaged = useCallback(
    (trimmed: string) => {
      const live = service.content;
      if (!live) return;
      const updated = deepCloneContent(live);
      updated.contentMetadata.contentName = trimmed;
      updated.contentData.href = '';
      updated.contentData.href = getNodeHref(updated.contentData, updated.contentMetadata) ?? '';
      service.stageEdit(updated);
    },
    [service],
  );

  // Builds a staged content snapshot with the updated short title and recomputed href.
  const buildShortTitleStaged = useCallback(
    (trimmed: string) => {
      const live = service.content;
      if (!live) return;
      const updated = deepCloneContent(live);
      updated.contentMetadata.contentShortName = trimmed;
      updated.contentData.href = '';
      updated.contentData.href = getNodeHref(updated.contentData, updated.contentMetadata) ?? '';
      service.stageEdit(updated);
    },
    [service],
  );

  const longTitle = useTitleField(
    service,
    initialLongTitle,
    longTitleInputMinLength,
    longTitleInputMaxLength,
    buildLongTitleStaged,
    mpl,
    contentId,
    convertEntitiesFn,
  );

  // Short title is required, same as the long title, but has duplicate checking
  // disabled — only the long title triggers duplicate search.
  const shortTitle = useTitleField(
    service,
    initialShortTitle,
    shortTitleInputMinLength,
    shortTitleInputMaxLength,
    buildShortTitleStaged,
    mpl,
    contentId,
    convertEntitiesFn,
    false,
  );

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  return { longTitle, shortTitle };
}
